export const LAN_IP_STORAGE_KEY = 'ogts-dashcam-lan-ip';

const IPV4_REGEX = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;

export function isValidIPv4(ip) {
  if (!ip || !IPV4_REGEX.test(ip.trim())) return false;
  return ip.trim().split('.').every((part) => {
    const n = parseInt(part, 10);
    return n >= 0 && n <= 255;
  });
}

export function getDefaultDevPort() {
  if (typeof window === 'undefined') return '3000';
  return window.location.port || '3000';
}

export function getStoredLanIp() {
  try {
    return localStorage.getItem(LAN_IP_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function storeLanIp(ip) {
  try {
    if (ip?.trim()) localStorage.setItem(LAN_IP_STORAGE_KEY, ip.trim());
  } catch {
    /* ignore quota / private mode */
  }
}

/** http://<LAN_IP>:<port>/?dash=true&dashcamPair=<moverId> */
export function buildDashcamPairingUrl(hostIp, port, moverId) {
  const ip = hostIp?.trim();
  if (!isValidIPv4(ip)) return '';

  const cleanPort = String(port || '3000').replace(/^:/, '');
  const params = new URLSearchParams();
  params.set('dash', 'true');
  if (moverId) params.set('dashcamPair', moverId);

  return `http://${ip}:${cleanPort}/?${params.toString()}`;
}

export function buildQrCodeImageUrl(pairingUrl) {
  if (!pairingUrl) return '';
  return `https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=8&data=${encodeURIComponent(pairingUrl)}`;
}

export function parseDashcamDeepLink(search) {
  const query =
    search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(query);
  return {
    dash: params.get('dash') === 'true',
    moverId: params.get('dashcamPair') || '',
  };
}

export function isLocalhostHostname(hostname = '') {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.local')
  );
}

/** Best-effort LAN IPv4 via WebRTC ICE (works on many desktop browsers). */
export function guessLocalLanIp() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.RTCPeerConnection) {
      resolve(null);
      return;
    }

    const found = new Set();
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    const finish = () => {
      pc.close();
      const candidates = [...found];
      const preferred =
        candidates.find((ip) => ip.startsWith('192.168.')) ||
        candidates.find((ip) => ip.startsWith('10.')) ||
        candidates.find((ip) => ip.startsWith('172.')) ||
        candidates[0] ||
        null;
      resolve(preferred);
    };

    pc.createDataChannel('ogts-dashcam-probe');
    pc.onicecandidate = (event) => {
      if (!event.candidate?.candidate) return;
      const matches = event.candidate.candidate.match(/([0-9]{1,3}(?:\.[0-9]{1,3}){3})/g);
      matches?.forEach((ip) => {
        if (isValidIPv4(ip) && !ip.startsWith('127.')) found.add(ip);
      });
    };

    pc.createOffer()
      .then((offer) => pc.setLocalDescription(offer))
      .catch(() => resolve(null));

    setTimeout(finish, 2800);
  });
}

export function resolveInitialLanIp() {
  const stored = getStoredLanIp();
  if (stored && isValidIPv4(stored)) return stored;

  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (!isLocalhostHostname(hostname) && isValidIPv4(hostname)) {
      return hostname;
    }
  }

  return '';
}
